import React, { useEffect, useRef, useState } from 'react'
import { Loader } from 'semantic-ui-react'

import environment from 'environment'

import { CardType } from 'types/deck'

import { useActions, useAppSelector } from 'redux/hooks'
import { selectCategoryList, useCanEditDeck } from 'redux/deck/selectors'
import { toggleModal as toggleModalAction } from 'redux/active/actions'

import SaveService from 'services/save.service'
import CardService from 'services/card.service'
import ToastService from 'services/toast.service'

import AddCardsModal from 'components/collectionV2/addCards/Modal'
import GlobalOverlayStack from 'components/elements/Overlay/GlobalOverlayStack'
import PhatButton from 'components/formElements/PhatButton'

import { firefoxNonAsciiRegEx } from './nonAsciiChar'

import styles from './importDropWrapper.module.scss'

type DropZones = 'automatic' | 'maybeboard' | 'sideboard' | 'existing' | 'new' | 'previous'

type Props = {
  children: React.ReactNode
  isCollection?: boolean
}

const ImportDropWrapper = ({ children, isCollection }: Props) => {
  const canEditDeck = useCanEditDeck()

  const [isLocalDrag, setIsLocalDrag] = useState(false)
  const [loading, setLoading] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const [isFirefox, setIsFirefox] = useState(false)
  const [previousCategory, setPreviousCategory] = useState<null | string>(null)
  const [defaultDraggedCategory, setDefaultDraggedCategory] = useState<DropZones>('automatic')
  const [chosenCategoryCard, setChooseCategoryCard] = useState<CardType | null>(null)

  const [addToCollectionCard, setAddToCollectionCard] = useState<CardType>()

  const [toggleModal] = useActions(toggleModalAction)

  const ownsCollection = useAppSelector(state => state.collectionV2.ownsCollection)
  const categories = useAppSelector(selectCategoryList)

  const canEdit = isCollection ? ownsCollection : canEditDeck

  const counter = useRef(0) // tracks how many dragEnter events have happened without a dragLeave, issue explained https://stackoverflow.com/questions/7110353/html5-dragleave-fired-when-hovering-a-child-element?page=1&tab=scoredesc#tab-top

  useEffect(() => {
    setIsFirefox(environment.getIsFirefox())

    const setDragging = () => setIsLocalDrag(true)
    const setDragDone = () => setIsLocalDrag(false)

    document.addEventListener('dragstart', setDragging)
    document.addEventListener('dragend', setDragDone)

    return () => {
      document.removeEventListener('dragstart', setDragging)
      document.removeEventListener('dragend', setDragDone)
    }
  }, [])

  const handleAddToDeck = (card: CardType) => SaveService.save({ ...card, qty: card.qty || 1 })

  const handleAddSelectedCategory = async (categoryName: string) => {
    if (!chosenCategoryCard) return

    handleAddToDeck({ ...chosenCategoryCard, categories: [categoryName] })
      .then(() => {
        setPreviousCategory(categoryName)
        setDefaultDraggedCategory('previous')
      })
      .catch(() =>
        ToastService.create('An error while trying to add card to existing category', 'External Drop Layer', 'error'),
      )
      .finally(() => setChooseCategoryCard(null))
  }

  const handleDocumentDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    if (!isDragging) return

    event.preventDefault()
    event.stopPropagation()

    if (!canEdit) return reset()

    try {
      const uidMatch = event.dataTransfer
        .getData('text/html')
        .replace(firefoxNonAsciiRegEx, '')
        .match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/)
      if (!uidMatch) {
        ToastService.create('We could not find any card IDs in that drop.', 'No ID found')
        return reset()
      }
      const uid = uidMatch[0]

      setLoading(true)

      const card = (
        await CardService.listCardsOnly({
          uids: [uid],
          includeArtCards: true,
          includeDigital: true,
          includeEmblems: true,
          includeTokens: true,
          includeOversized: true,
        })
      )[0]

      if (!card) {
        ToastService.create('No cards matched the uid we found in the drop.', 'Card not found')
        return reset()
      }

      // Note we're returning here, allow the collection add form to handle the rest
      if (isCollection) {
        setAddToCollectionCard(card)

        return reset()
      }

      if (defaultDraggedCategory === 'new') {
        toggleModal('newCategoryDrop', { card })

        return reset()
      }

      if (defaultDraggedCategory === 'existing') {
        setChooseCategoryCard(card)

        return reset()
      }

      if (defaultDraggedCategory === 'previous' && previousCategory) card.categories = [previousCategory]
      else if (defaultDraggedCategory === 'maybeboard') card.categories = ['Maybeboard']
      else if (defaultDraggedCategory === 'sideboard') card.categories = ['Sideboard']

      if (defaultDraggedCategory !== 'previous') setPreviousCategory(null)

      await handleAddToDeck(card)

      reset()
    } catch {
      ToastService.create(
        "An error occurred while trying to import the card. This could be caused by a few things. Something happened on our end, the card you dragged in isn't on Archidekt yet, etc. If the problem continues please contact support. ",
        'External Drop Layer',
        'error',
      )

      reset()
    }
  }

  const reset = () => {
    setIsDragging(false)
    setIsLocalDrag(false)
    counter.current = 0
    setLoading(false)
  }

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    if (
      isFirefox &&
      (event.dataTransfer.getData('application/json') ||
        event.dataTransfer.getData('text/html').includes('basicCardImage'))
    )
      return
    if (isLocalDrag) return
    event.preventDefault()
    event.stopPropagation()

    if (!isDragging && canEdit && !chosenCategoryCard) setIsDragging(true)
  }

  const handleDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()
    counter.current++
  }

  const handleDragEnd = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()
    event.stopPropagation()
    counter.current--
    if (counter.current === 0) {
      setIsLocalDrag(false)
      setIsDragging(false)
    }
  }

  if (!canEdit) return <>{children}</>

  return (
    <div
      onDrop={handleDocumentDrop}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragEnd}>
      <div id="external-drag-id" className={`${styles.overlay} ${isDragging && styles.visible}`}>
        {!loading && (
          <>
            <h3>Drop to import an external card...</h3>

            {!isCollection && (
              <>
                <div className={styles.dropzones}>
                  <div
                    className={`${styles.dropzone} ${defaultDraggedCategory === 'automatic' ? styles.selected : ''}`}
                    onDragEnter={() => setDefaultDraggedCategory('automatic')}>
                    Automatic
                  </div>
                  <div
                    className={`${styles.dropzone} ${defaultDraggedCategory === 'maybeboard' ? styles.selected : ''}`}
                    onDragEnter={() => setDefaultDraggedCategory('maybeboard')}>
                    Maybeboard
                  </div>
                  <div
                    className={`${styles.dropzone} ${defaultDraggedCategory === 'sideboard' ? styles.selected : ''}`}
                    onDragEnter={() => setDefaultDraggedCategory('sideboard')}>
                    Sideboard
                  </div>
                </div>

                <div className={styles.spacer} />

                <div className={styles.dropzones}>
                  <div
                    className={`${styles.dropzone} ${defaultDraggedCategory === 'previous' ? styles.selected : ''} ${!previousCategory ? styles.disabled : ''}`}
                    onDragEnter={() => (previousCategory ? setDefaultDraggedCategory('previous') : null)}>
                    {previousCategory || 'Chosen category'}
                  </div>

                  <div
                    className={`${styles.dropzone} ${defaultDraggedCategory === 'existing' ? styles.selected : ''}`}
                    onDragEnter={() => setDefaultDraggedCategory('existing')}>
                    Choose category
                  </div>

                  <div
                    className={`${styles.dropzone} ${defaultDraggedCategory === 'new' ? styles.selected : ''}`}
                    onDragEnter={() => setDefaultDraggedCategory('new')}>
                    New category
                  </div>
                </div>
              </>
            )}
          </>
        )}
        {loading && <Loader active inverted />}
      </div>
      {children}

      {/* CollectionV2 adding overlay */}

      <AddCardsModal
        open={!!addToCollectionCard}
        cardToAdd={addToCollectionCard}
        onClose={() => setAddToCollectionCard(undefined)}
      />

      <GlobalOverlayStack open={!!chosenCategoryCard} onClose={() => setChooseCategoryCard(null)} noWeirdClose>
        <div className={styles.categoriesOverlay}>
          <h3>Choose category to import into...</h3>
          <div className={styles.categories}>
            {Object.values(categories).map(category => (
              <PhatButton onClick={() => handleAddSelectedCategory(category.name)} key={category.id}>
                {category.name}
              </PhatButton>
            ))}
          </div>
        </div>
      </GlobalOverlayStack>
    </div>
  )
}

export default ImportDropWrapper
